import { Formik, FormikHelpers } from 'formik';
import { observer } from 'mobx-react-lite';
import * as Yup from 'yup';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../stores/store';
import FormFieldText from '../../common/form-field-text/form-field-text.component';
import Button from '../../common/button/button.component';
import { EditFormProps } from '../../features/services/services-subsection/services-subsection.component';
import { AddressDataValues } from '../../models/addressDataValues';
import {
  createValidationRule,
  isInitialValuesEqualCurrentValues,
} from '../../utils/forms/forms.utils';
import CountriesFormFieldSearch from '../../common/form-field-countries-search/form-field-countries-search.component';
import { AccountSettingsForm } from '../../common/form/form.styles';
import {
  VALIDATION_RULE_LATIN_EXTENDED_WITH_SPECIAL_CHARS,
  VALIDATION_RULE_LATIN_WITH_COMMA,
  VALIDATION_RULE_LATIN_WITH_NUMBERS,
  VALIDATION_RULE_ONLY_LETTERS,
} from '../../constants/validation';
import PlacesFormFieldSearch from '../../common/form-field-places-search/form-field-places-search.component';
import {
  getPostalCodeLabel,
  getRegionLabel,
} from '../../features/parcel-creation/delivery-details-form/delivery-details-form.component';
import InputSearchWithSidebarForm from '../../common/input-search-with-sidebar/input-search-with-sidebar-form.component';
import { ReactComponent as ArrowRightIcon } from '../../assets/arrow-right-icon.svg';
import { findAdminUnitFromPrediction } from '../../utils/countries/countries.utils';
import { isFeatureRequired } from '../../utils/client/client.utils';
import { RevalidateOnLangChange } from '../../common/form/form.helpers';
import FormCheckbox from '../../common/checkbox/checkbox-form.component';

type IFormValues = AddressDataValues;

const MyAddressEditForm = ({
  onSubmit,
  onCancel,
  address,
}: EditFormProps & {
  address?: AddressDataValues;
  onSubmit?: (address: AddressDataValues) => void;
}) => {
  const {
    userStore: { createAddress, updateSenderAddress },
    localizationsStore: { findCountryByCode, selectedCountry: selectedLocale },
    placesSearchStore: { setUserPlaceDetails, setPlaceDetails },
    commonStore: { toastSuccess },
  } = useStore();

  const { t } = useTranslation();

  function getPostalCodeValidationRule() {
    let postalCodeValidationRule = createValidationRule(
      VALIDATION_RULE_LATIN_WITH_COMMA
    ).required('this_field_cannot_be_empty');

    postalCodeValidationRule = postalCodeValidationRule.test(
      'zip_min_length',
      `${t('create_parcel_length_short')} 6`,
      function (value) {
        if (!value) return false;
        // eslint-disable-next-line react/no-this-in-sfc
        if (!isFeatureRequired('zipMinLengthSixChars', this.parent.country))
          return true;

        return value.replace(/\s/g, '').length >= 6;
      }
    );

    return postalCodeValidationRule;
  }

  const validationSchema = Yup.object({
    title: createValidationRule(VALIDATION_RULE_LATIN_WITH_NUMBERS).required(
      'this_field_cannot_be_empty'
    ),
    country: Yup.string().required('this_field_cannot_be_empty'),
    region: createValidationRule(VALIDATION_RULE_LATIN_WITH_COMMA).required(
      'this_field_cannot_be_empty'
    ),
    city: createValidationRule(VALIDATION_RULE_LATIN_WITH_COMMA).required(
      'this_field_cannot_be_empty'
    ),
    post_code: getPostalCodeValidationRule(),
    street: createValidationRule(VALIDATION_RULE_LATIN_WITH_COMMA).required(
      'this_field_cannot_be_empty'
    ),
    full_address: createValidationRule(
      VALIDATION_RULE_LATIN_WITH_COMMA
    ).notRequired(),
    building: createValidationRule(
      VALIDATION_RULE_LATIN_EXTENDED_WITH_SPECIAL_CHARS
    ).required('this_field_cannot_be_empty'),
    apartment: createValidationRule(
      VALIDATION_RULE_LATIN_WITH_COMMA
    ).notRequired(),
    note: createValidationRule(VALIDATION_RULE_LATIN_WITH_COMMA).notRequired(),
  });

  useEffect(() => {
    // On the first run init place values in PlacesSearchStore based on current user.
    // Then they will be updated based on response from Places API
    setUserPlaceDetails();

    // Clear PlacesSearchStore when form is closed
    return () => {
      setPlaceDetails(null);
    };
  }, [setPlaceDetails, setUserPlaceDetails]);

  const [selectedCountry, setSelectedCountry] = useState(() =>
    address
      ? findCountryByCode(address.country)
      : findCountryByCode(selectedLocale!.country_code)
  );

  const initialValues = useMemo(
    () =>
      ({
        id: address?.id || -1,
        title: address?.title,
        country: selectedCountry?.country_code || '',
        full_address: address?.full_address || '',
        region: address?.region || '',
        city: address?.city || '',
        post_code: address?.post_code || '',
        street: address?.street || '',
        building: address?.building || '',
        apartment: address?.apartment || '',
        note: address?.note || '',
        is_default: address?.is_default,
      }) as IFormValues,
    [
      address?.id,
      address?.title,
      selectedCountry?.country_code,
      address?.apartment,
      address?.building,
      address?.city,
      address?.full_address,
      address?.note,
      address?.post_code,
      address?.region,
      address?.street,
      address?.is_default,
    ]
  );

  const handleFormSubmit = async (
    formValues: IFormValues,
    formikHelpers: FormikHelpers<IFormValues>
  ) => {
    if (isInitialValuesEqualCurrentValues(initialValues, formValues)) {
      onCancel?.();
      return;
    }
    const scrollPosition = window.scrollY;

    formikHelpers.setFieldTouched('region', true, true);

    requestAnimationFrame(() => {
      window.scrollTo({ top: scrollPosition, behavior: 'auto' });
    });

    if (!address) {
      const createdAddress = await createAddress(formValues);
      if (createdAddress) {
        toastSuccess(t('toast_new_address_added'));
        onSubmit?.(createdAddress);
      }
    }

    if (address) {
      const updateSenderAdress = await updateSenderAddress(formValues);
      if (updateSenderAdress) {
        toastSuccess(t('data_updated_successfully'));
        onSubmit?.(updateSenderAdress);
      }
    }

    onCancel?.();
    formikHelpers.resetForm();
  };

  const adminUnits = selectedCountry?.administrative_units?.allowed_values;
  const adminUnitsTitle = selectedCountry?.administrative_units?.title;

  return (
    <Formik
      validationSchema={validationSchema}
      initialValues={initialValues}
      onSubmit={handleFormSubmit}>
      {({
        handleSubmit,
        values,
        isSubmitting,
        setValues,
        validateField,
        validateForm,
        setFieldValue,
        resetForm,
        setStatus,
        status,
      }) => {
        const onChange = (_: React.ChangeEvent<HTMLInputElement>) => {
          setFieldValue('full_address', '', false);
        };
        const validationMethod = status === 'submit' ? 'always' : 'default';
        return (
          <AccountSettingsForm
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              setStatus('submit');
              validateForm(values);
              handleSubmit();
            }}>
            <div className='inputs'>
              <FormFieldText
                maxLength={100}
                name='title'
                label='address_name'
                validationMethod={validationMethod}
                onChange={onChange}
                showMaxLengthCounter
              />

              <CountriesFormFieldSearch
                initialCountry={selectedCountry}
                name='country'
                label={t('country')}
                onCountrySelected={(c) => {
                  const newCountry = findCountryByCode(c);
                  if (
                    !!newCountry?.country_code &&
                    selectedCountry?.country_code !== newCountry.country_code
                  ) {
                    // Resets places search store in case country was changed and therefore initial values and as a result whole form state is reset.
                    setPlaceDetails(null);
                    resetForm({
                      values: {
                        id: -1,
                        title: values.title,
                        country: newCountry?.country_code || '',
                        full_address: '',
                        region: '',
                        city: '',
                        post_code: '',
                        street: '',
                        building: '',
                        section: '',
                        apartment: '',
                        buzz_code: '',
                        note: '',
                        is_default: false,
                      },
                    });
                  }
                  setSelectedCountry(newCountry!);
                }}
              />
              <PlacesFormFieldSearch
                selectedCountryCode={selectedCountry?.country_code}
                name='full_address'
                placeholder=''
                label={t('address_search')}
                formikValidateField={validateField}
                validationRule='no-rule'
                isOptional
                setValues={(placeInfo) => {
                  const region =
                    findAdminUnitFromPrediction(placeInfo.region, adminUnits) ??
                    '';
                  setValues({
                    ...values,
                    full_address: placeInfo.address,
                    region,
                    city: placeInfo.city,
                    street: placeInfo.street,
                    post_code: placeInfo.zipCode,
                    building: placeInfo.building,
                    apartment: placeInfo.apartment,
                  });
                }}
              />

              {adminUnits && adminUnits.length > 0 ? (
                <InputSearchWithSidebarForm
                  name='region'
                  inputIcon={ArrowRightIcon}
                  shouldValidateSearchQuery
                  displayAllOptionsWithEmptyFilter
                  inputValue={values.region ?? ''}
                  defaultOption={values.region ?? ''}
                  isOptionSelected={(region) => region === values.region}
                  label={adminUnitsTitle || ''}
                  sidebarTitle={adminUnitsTitle || ''}
                  sidebarInputPlaceholder={t('region_search_placeholder', {
                    region: getRegionLabel(
                      selectedCountry?.country_code
                    ).toLowerCase(),
                  })}
                  placeholder={t('select_region', {
                    region: getRegionLabel(
                      selectedCountry?.country_code
                    ).toLowerCase(),
                  })}
                  getSearchOptions={(filter) => {
                    const userRegion = address?.region;
                    const adminUnitsValues = adminUnits?.map((i) => i.value);
                    const regions = filter
                      ? adminUnitsValues!.filter((i) =>
                          i
                            .toLowerCase()
                            .includes(filter.toLocaleLowerCase().trim())
                        )
                      : adminUnitsValues;
                    if (!userRegion) return Promise.resolve(regions);

                    const found = regions!.find(
                      (region) => userRegion === region
                    );
                    if (found) {
                      const filteredArray = regions!.filter(
                        (region) => region !== userRegion
                      );
                      return Promise.resolve([found, ...filteredArray]);
                    }
                    return Promise.resolve(regions);
                  }}
                  onSearchOptionSelected={(selectedOption) => {
                    setFieldValue('full_address', '');
                    setFieldValue('region', selectedOption, true);
                  }}
                  getKeyForSearchOption={(option) => option}
                  getDisplayValueForSearchOption={(option) => option}
                  validationRule={VALIDATION_RULE_ONLY_LETTERS}
                  validationMethod='touched'
                />
              ) : (
                <FormFieldText
                  maxLength={100}
                  name='region'
                  placeholder=''
                  onChange={onChange}
                  validationMethod={validationMethod}
                  label={adminUnitsTitle || ''}
                  showMaxLengthCounter
                />
              )}

              <FormFieldText
                maxLength={10}
                name='post_code'
                placeholder=''
                onChange={onChange}
                validationMethod={validationMethod}
                label={getPostalCodeLabel(selectedCountry?.country_code)}
                showMaxLengthCounter
              />

              <FormFieldText
                maxLength={100}
                name='city'
                placeholder=''
                label='city'
                onChange={onChange}
                validationMethod={validationMethod}
                showMaxLengthCounter
              />

              <FormFieldText
                maxLength={100}
                name='street'
                label='street'
                validationMethod={validationMethod}
                onChange={onChange}
                showMaxLengthCounter
              />

              <FormFieldText
                maxLength={20}
                name='building'
                label='building'
                onChange={onChange}
                validationMethod={validationMethod}
                showMaxLengthCounter
              />

              <FormFieldText
                maxLength={10}
                name='apartment'
                label='apartment'
                onChange={onChange}
                validationMethod={validationMethod}
                isOptional
                showMaxLengthCounter
              />

              <FormCheckbox
                name='is_default'
                disabled={address?.is_default}
                validationMethod={validationMethod}>
                {t('seve_address_as_default')}
              </FormCheckbox>
            </div>
            <div className='buttons'>
              <Button
                appearance='secondary'
                disabled={isSubmitting}
                onClick={() => onCancel && onCancel()}>
                {t('cancel')}
              </Button>
              <Button
                isLoading={isSubmitting}
                type='submit'>
                {t('save')}
              </Button>
            </div>
            <RevalidateOnLangChange />
          </AccountSettingsForm>
        );
      }}
    </Formik>
  );
};

export default observer(MyAddressEditForm);
